/** Normalize Content-ID / cid: src values for lookup (case-insensitive). */
export function normalizeContentId(raw) {
  if (raw == null || raw === '') return '';
  let s = String(raw).trim();
  if (/^cid:/i.test(s)) s = s.slice(4);
  s = s.replace(/^<|>$/g, '').trim();
  return s.toLowerCase();
}

/** Authenticated Gmail attachment URL — works in <img src> on same origin (session cookie). */
export function gmailAttachmentUrl(mailboxId, messageId, attachmentId, apiBase = '') {
  return `${apiBase}/api/gmail/mailboxes/${encodeURIComponent(mailboxId)}/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`;
}

function attachmentContentId(att) {
  if (!att) return '';
  return normalizeContentId(
    att.content_id || att.contentId || att.contentID || att.cid || '',
  );
}

export function buildCidAttachmentMap(attachments) {
  const map = new Map();
  (Array.isArray(attachments) ? attachments : []).forEach((att) => {
    if (!att || !att.attachmentId) return;
    const cid = attachmentContentId(att);
    if (cid) map.set(cid, att);
  });
  return map;
}

function isImageAttachment(att) {
  const ct = (att && (att.content_type || att.mimeType)) || '';
  if (/^image\//i.test(ct)) return true;
  const fn = (att && att.filename) || '';
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fn);
}

function imageAttachmentsForInline(attachments) {
  return (Array.isArray(attachments) ? attachments : [])
    .filter((a) => a && a.attachmentId && isImageAttachment(a));
}

/** Resolve a cid: src to an attachment — Content-ID map, then ordered / sole-image heuristics. */
export function resolveInlineImageAttachment(cidRaw, attachments, { cidMap, cidIndex = 0, cidCount = 1 } = {}) {
  const map = cidMap || buildCidAttachmentMap(attachments);
  const key = normalizeContentId(cidRaw);
  if (key && map.has(key)) return map.get(key);

  const imageAtts = imageAttachmentsForInline(attachments);
  if (cidCount > 0 && imageAtts.length === cidCount && cidIndex < imageAtts.length) {
    return imageAtts[cidIndex];
  }
  if (cidCount === 1 && imageAtts.length === 1) return imageAtts[0];
  return null;
}

/** Max edge length (px) for decorative/tracker imgs with no alt — hide when load fails. */
export const DECORATIVE_IMAGE_MAX_PX = 48;

export function parseImgDimensionPx(value) {
  if (value == null || value === '') return null;
  const n = parseInt(String(value).trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Email UX: decorative when empty alt and both declared dimensions ≤ threshold (or unset). */
export function isDecorativeEmailImgAttrs({ alt, width, height, naturalWidth, naturalHeight } = {}) {
  if (String(alt || '').trim()) return false;
  const w = parseImgDimensionPx(width);
  const h = parseImgDimensionPx(height);
  if (w != null || h != null) {
    const maxDeclared = Math.max(w || 0, h || 0);
    return maxDeclared > 0 && maxDeclared <= DECORATIVE_IMAGE_MAX_PX;
  }
  if (naturalWidth > 0 && naturalHeight > 0) {
    return naturalWidth <= DECORATIVE_IMAGE_MAX_PX && naturalHeight <= DECORATIVE_IMAGE_MAX_PX;
  }
  return false;
}

/** Fallback label when an inline image cannot load — prefer meaningful alt (email best practice). */
export function failedEmailImgFallbackLabel({ alt, title } = {}) {
  const altText = String(alt || '').trim();
  if (altText) return altText;
  const titleText = String(title || '').trim();
  if (titleText) return titleText;
  return 'Image unavailable';
}

/** After load error — hide decorative imgs; show alt or placeholder for content imgs. */
export function applyFailedEmailImgFallback(img) {
  if (!img || img.dataset.emailImgFailed) return;
  img.dataset.emailImgFailed = '1';

  const attrs = {
    alt: img.getAttribute('alt'),
    width: img.getAttribute('width'),
    height: img.getAttribute('height'),
    naturalWidth: img.naturalWidth,
    naturalHeight: img.naturalHeight,
  };

  if (isDecorativeEmailImgAttrs(attrs)) {
    img.style.display = 'none';
    return;
  }

  const span = document.createElement('span');
  span.className = 'email-body-img-unavailable';
  span.textContent = `[${failedEmailImgFallbackLabel({
    alt: attrs.alt,
    title: img.getAttribute('title'),
  })}]`;
  const w = parseImgDimensionPx(attrs.width);
  if (w) span.style.maxWidth = `${w}px`;
  img.replaceWith(span);
}

// cid: with or without angle brackets, e.g. cid:logo@x or cid:<logo@x>
const IMG_SRC_CID_RE = /(<img\b[^>]*?\bsrc\s*=\s*)(["']?)(cid:(?:[^"'>]+|<[^">]+>))\2/gi;
const IMG_HTTP_SRC_RE = /(<img\b[^>]*?\bsrc\s*=\s*["'])http:\/\//gi;

function countCidImages(html) {
  let n = 0;
  const re = new RegExp(IMG_SRC_CID_RE.source, 'gi');
  html.replace(re, () => { n += 1; return ''; });
  return n;
}

/** Pull &lt;style&gt; blocks from a full HTML document (marketing emails). */
export function extractEmailHtmlHeadStyles(html) {
  const raw = html != null ? String(html) : '';
  if (!raw.trim()) return '';
  const styles = [];
  const re = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  let m;
  while ((m = re.exec(raw)) !== null) {
    styles.push(`<style>${m[1]}</style>`);
  }
  return styles.join('\n');
}

function isStylesheetLinkTag(tag) {
  return /\brel=(["'])([^"']*\bstylesheet\b[^"']*)\1/i.test(tag)
    || /\brel=stylesheet\b/i.test(tag);
}

/** Pull &lt;link rel="stylesheet"&gt; tags from email HTML documents. */
function extractEmailHtmlHeadLinks(html) {
  const raw = html != null ? String(html) : '';
  if (!raw.trim()) return '';
  const links = [];
  const re = /<link\b[^>]*>/gi;
  let m;
  while ((m = re.exec(raw)) !== null) {
    if (isStylesheetLinkTag(m[0])) links.push(m[0]);
  }
  return links.join('\n');
}

/** Preserve body tag presentation attrs when lifting body inner HTML into an iframe. */
export function parseEmailBodyOpenAttrs(html) {
  const m = String(html || '').match(/<body([^>]*)>/i);
  if (!m) return '';
  const attrs = m[1] || '';
  const out = [];
  const style = attrs.match(/\bstyle=(["'])([\s\S]*?)\1/i);
  let styleVal = style ? style[2].trim() : '';
  const bg = attrs.match(/\bbgcolor=(["']?)([^"'>\s]+)\1/i);
  if (bg) {
    const bgCss = `background-color:${bg[2]}`;
    styleVal = styleVal ? `${styleVal.replace(/;$/, '')};${bgCss}` : bgCss;
  }
  if (styleVal) out.push(` style="${styleVal.replace(/"/g, '&quot;')}"`);
  const cls = attrs.match(/\bclass=(["'])([\s\S]*?)\1/i);
  if (cls) out.push(` class="${cls[2].replace(/"/g, '&quot;')}"`);
  return out.join('');
}

/** Strip document wrapper — return embeddable body markup (handles Gmail full-document body_html). */
export function unwrapEmailHtmlDocument(html) {
  const raw = html != null ? String(html) : '';
  if (!raw.trim()) return '';
  if (!/<html[\s>]/i.test(raw) && !/<!DOCTYPE/i.test(raw)) return raw;

  const bodyOpen = raw.match(/<body[^>]*>/i);
  if (bodyOpen) {
    const start = bodyOpen.index + bodyOpen[0].length;
    const closeRe = /<\/body>/gi;
    let lastClose = -1;
    let m;
    while ((m = closeRe.exec(raw)) !== null) {
      lastClose = m.index;
    }
    if (lastClose > start) {
      return raw.slice(start, lastClose);
    }
  }

  return raw
    .replace(/<!DOCTYPE[^>]*>/gi, '')
    .replace(/<\/?html[^>]*>/gi, '')
    .replace(/<head[\s\S]*?<\/head>/gi, '')
    .replace(/<\/?body[^>]*>/gi, '');
}

const NOTE_IFRAME_BASE_STYLES = `
  html, body { margin: 0; padding: 0; width: 100%; overflow-x: auto; box-sizing: border-box; }
  *, *::before, *::after { box-sizing: inherit; }
  body { font-family: Roboto, Helvetica, Arial, sans-serif; font-size: 14px; line-height: 1.4; padding: 8px 12px 12px; }
  img { max-width: 100% !important; height: auto !important; }
  table { max-width: 100% !important; }
  .email-note-header { margin: 0 0 10px; padding: 0 8px; font-size: 0.8125rem; line-height: 1.35; color: rgba(0, 0, 0, 0.78); }
  .email-note-header p { margin: 0 0 2px; }
  .email-note-header hr { margin: 6px 0; border: 0; border-top: 1px solid rgba(0, 0, 0, 0.12); }
  .email-body-plain { padding: 0; white-space: pre-wrap; word-break: break-word; }
  .email-quote-block { display: block; color: rgba(0, 0, 0, 0.45); border-left: 2px solid rgba(0, 0, 0, 0.18); padding-left: 8px; }
  .email-quote-attr { display: block; color: rgba(0, 0, 0, 0.55); font-style: italic; }
  .email-body-img-unavailable {
    color: rgba(0, 0, 0, 0.54); display: inline-block; font-size: 0.75rem;
    font-style: italic; line-height: 1.4; word-break: break-word;
  }
`;

function noteContentLooksLikeHtml(raw) {
  return /<\/?[a-z][\s\S]*>/i.test(raw);
}

function escapeHtmlText(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const HTML_NAMED_ENTITIES = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  quot: '"',
  nbsp: '\u00a0',
  rsquo: '\u2019',
  lsquo: '\u2018',
  rdquo: '\u201d',
  ldquo: '\u201c',
  ndash: '\u2013',
  mdash: '\u2014',
  hellip: '\u2026',
  copy: '\u00a9',
  reg: '\u00ae',
  trade: '\u2122',
  bull: '\u2022',
};

function decodeHtmlEntitiesOnce(text) {
  return String(text)
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => {
      const cp = parseInt(hex, 16);
      try {
        return String.fromCodePoint(cp);
      } catch (e) {
        return `&#x${hex};`;
      }
    })
    .replace(/&#(\d+);/g, (_, num) => {
      const cp = Number(num);
      try {
        return String.fromCodePoint(cp);
      } catch (e) {
        return `&#${num};`;
      }
    })
    .replace(/&([a-z]+);/gi, (match, name) => {
      const ch = HTML_NAMED_ENTITIES[name.toLowerCase()];
      return ch !== undefined ? ch : match;
    });
}

/** 22 Sep 2026 JCH: NC-1641 — stored &lt; / &rsquo; / &#8217; on old email-in. Regex only, three
 * passes (&amp;lt; → <). A textarea decoder parses the note as HTML. */
export function decodeHtmlEntitiesRegex(text) {
  let out = text == null ? '' : String(text);
  if (!/&(?:#x?[0-9a-f]+|[a-z]+);/i.test(out)) return out;
  for (let i = 0; i < 3; i += 1) {
    const next = decodeHtmlEntitiesOnce(out);
    if (next === out) break;
    out = next;
  }
  return out;
}

/** "On <date> <name> wrote:" attribution line above quoted text (Gmail plain-text replies). */
const QUOTE_ATTRIBUTION_RE = /^On .{0,160}wrote:\s*$/;

/** 25 Aug 2026 JCH: NC-1641 — display-only markup for plain email text (never stored): quote runs
 * grouped/grayed, blank quote lines squeezed, "On ... wrote:" italicized. */
export function plainEmailTextToHtml(text) {
  const s = text != null ? String(text) : '';
  if (!s.trim()) return '';
  const html = [];
  let quoteRun = [];
  let blankQuotes = 0;
  const flushQuotes = () => {
    if (quoteRun.length) html.push(`<span class="email-quote-block">${quoteRun.join('<br>')}</span>`);
    quoteRun = [];
    blankQuotes = 0;
  };
  s.split(/\r\n|\r|\n/).forEach((line) => {
    const shown = decodeHtmlEntitiesRegex(line);
    const esc = escapeHtmlText(shown);
    if (/^\s*>/.test(shown)) {
      if (/^[\s>]*$/.test(shown)) { // quote marks only — Gmail's blank quoted lines
        blankQuotes += 1;
        if (blankQuotes > 1) return;
      } else {
        blankQuotes = 0;
      }
      quoteRun.push(esc);
      return;
    }
    flushQuotes();
    if (QUOTE_ATTRIBUTION_RE.test(shown)) {
      html.push(`<span class="email-quote-attr">${esc}</span>`);
    } else {
      html.push(`${esc}<br>`);
    }
  });
  flushQuotes();
  // 18 Sep 2026 JCH: NC-1641 — breaks go BETWEEN lines; one after the last line only adds a blank at the bottom
  const last = html.length - 1;
  if (last >= 0 && html[last].endsWith('<br>')) html[last] = html[last].slice(0, -4);
  return `<div class="email-body-plain">${html.join('')}</div>`;
}

/** Single srcdoc document for note iframe — merges header fragment + unwrapped email HTML.
 * 25 Aug 2026 JCH: NC-1641 — treatAsHtml false renders escaped plain text: the looks-like-HTML
 * guess mistook <addresses> for tags, which ate them and ran the line breaks together. */
export function buildNoteIframeSrcdoc(noteContent, { treatAsHtml, padDocument } = {}) {
  const raw = noteContent != null ? String(noteContent) : '';
  if (!raw.trim()) return '';
  if (treatAsHtml === false) {
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank">`
      + `<meta name="viewport" content="width=device-width, initial-scale=1">`
      + `<style>${NOTE_IFRAME_BASE_STYLES}</style>`
      + `</head><body>${plainEmailTextToHtml(raw)}</body></html>`;
  }

  const extraStyles = extractEmailHtmlHeadStyles(raw);
  const extraLinks = extractEmailHtmlHeadLinks(raw);
  let inner = raw;
  let bodyAttrs = '';
  const htmlIdx = raw.search(/<html[\s>]/i);
  if (htmlIdx >= 0) {
    const header = raw.slice(0, htmlIdx);
    const docPart = raw.slice(htmlIdx);
    bodyAttrs = parseEmailBodyOpenAttrs(docPart);
    inner = header + unwrapEmailHtmlDocument(docPart);
  } else if (/<!DOCTYPE/i.test(raw)) {
    bodyAttrs = parseEmailBodyOpenAttrs(raw);
    inner = unwrapEmailHtmlDocument(raw);
  } else if (!noteContentLooksLikeHtml(raw)) {
    inner = plainEmailTextToHtml(raw);
  }

  if (extraStyles) {
    inner = inner.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  }
  if (extraLinks) {
    inner = inner.replace(/<link\b[^>]*>/gi, (tag) => (isStylesheetLinkTag(tag) ? '' : tag));
  }

  // 27 Aug 2026 JCH: NC-1641 — web pages zero their own body margins inline; !important restores gutters
  const padStyle = padDocument ? '<style>body { padding: 12px 16px !important; }</style>' : '';
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank">`
    + `<meta name="viewport" content="width=device-width, initial-scale=1">`
    + `<style>${NOTE_IFRAME_BASE_STYLES}</style>${extraStyles}${extraLinks}${padStyle}`
    + `</head><body${bodyAttrs}>${inner}</body></html>`;
}

/** Measure rendered document height inside an iframe. */
export function measureIframeContentHeight(iframe) {
  if (!iframe || !iframe.contentDocument) return 0;
  const doc = iframe.contentDocument;
  const body = doc.body;
  const html = doc.documentElement;
  const viewH = iframe.clientHeight || 0;
  // 21 Sep 2026 JCH: NC-1641 — html/body offsetHeight (and body.scrollHeight when it isn't
  // overflowing) are the iframe *window*, not the note. Counting them kept a short expand at the
  // starting clamp (~40vh) — the tall empty box on a call-log web note.
  const heights = [
    html && html.scrollHeight > html.clientHeight ? html.scrollHeight : 0,
    // viewH can be 0 while hidden; still count an overflowing body
    body && body.scrollHeight > viewH ? body.scrollHeight : 0,
  ];
  if (body && body.lastElementChild) {
    const last = body.lastElementChild.getBoundingClientRect();
    const bodyTop = body.getBoundingClientRect().top;
    heights.push(last.bottom - bodyTop + (body.scrollTop || 0));
  }
  if (body) {
    try {
      const range = doc.createRange();
      range.selectNodeContents(body);
      const r = range.getBoundingClientRect();
      if (r.height) heights.push(r.height);
    } catch (e) { /* Range not available */ }
  }
  return Math.max(...heights.filter((h) => Number.isFinite(h) && h > 0), 0);
}

/** Size iframe to its document height (cap optional). */
export function resizeIframeToContent(iframe, { maxHeight = 2400, minHeight = 320 } = {}) {
  if (!iframe || !iframe.contentDocument) return;
  const h = measureIframeContentHeight(iframe);
  // 21 Sep 2026 JCH: NC-1641 — Firefox fires srcdoc load twice; the second pass can be an empty
  // document (measure 0). Collapsing to minHeight (80px) made the call-log expand vanish.
  if (h <= 0) return;
  const cap = Number.isFinite(maxHeight) && maxHeight > 0 ? maxHeight : Infinity;
  const overflowY = h + 8 > cap ? 'auto' : 'hidden';
  if (iframe.style.overflowY !== overflowY) iframe.style.overflowY = overflowY;
  // 25 Aug 2026 JCH: NC-1641 — write height only on real change (±2px), or our own write loops the observer
  const target = Math.min(Math.max(h + 8, minHeight), cap);
  if (iframe._nuLastAutoHeight != null && Math.abs(iframe._nuLastAutoHeight - target) <= 2) return;
  iframe._nuLastAutoHeight = target;
  iframe.style.height = `${target}px`;
}

/** Re-measure after layout/images so long marketing emails are not clipped. */
export function bindIframeAutoResize(iframe, options = {}) {
  if (!iframe) return;
  const run = () => resizeIframeToContent(iframe, options);
  // 21 Sep 2026 JCH: NC-1641 — measure twice (layout can settle one frame later), then unhide so
  // the expand isn't shown at the stub height and twitched by the second pass.
  const afterLayout = () => {
    run();
    if (options.reveal && iframe._nuLastAutoHeight) {
      iframe.style.position = '';
      iframe.style.left = '';
      iframe.style.top = '';
      iframe.style.visibility = 'visible';
      if (typeof options.onReveal === 'function') options.onReveal();
    }
  };
  run();
  if (typeof requestAnimationFrame !== 'undefined') {
    requestAnimationFrame(afterLayout);
  } else {
    afterLayout();
  }
  try {
    const doc = iframe.contentDocument;
    if (doc && doc.body) {
      let pendingImgs = 0;
      doc.querySelectorAll('img').forEach((img) => {
        if (!img.complete) {
          pendingImgs += 1;
          img.addEventListener('load', run, { once: true });
        }
        img.addEventListener('error', run, { once: true });
      });
      // 21 Sep 2026 JCH: NC-1641 — late passes and ResizeObserver are for banners still loading.
      // On a text-only expand they just nudged the box after it already fit.
      if (pendingImgs) {
        setTimeout(run, 100);
        setTimeout(run, 500);
        setTimeout(run, 1500);
        setTimeout(run, 3000);
        if (typeof ResizeObserver !== 'undefined') {
          // 25 Aug 2026 JCH: NC-1641 — resize next frame; same-frame writes log "ResizeObserver loop" errors
          const ro = new ResizeObserver(() => {
            if (typeof requestAnimationFrame !== 'undefined') requestAnimationFrame(run);
            else run();
          });
          ro.observe(doc.body);
          iframe._nuIframeResizeObserver = ro;
        }
      }
    }
  } catch (e) { /* cross-origin guard */ }
}

/** Rewrite cid: inline images to API attachment URLs; harden external http/https imgs. */
export function prepareEmailBodyHtml(html, { mailboxId, messageId, attachments, apiBase = '' } = {}) {
  const raw = html != null ? String(html) : '';
  if (!raw.trim() || !mailboxId || !messageId) return raw;

  const cidMap = buildCidAttachmentMap(attachments);
  const cidCount = countCidImages(raw);
  let cidIndex = 0;

  let out = raw.replace(IMG_SRC_CID_RE, (match, prefix, quote, cidSrc) => {
    const att = resolveInlineImageAttachment(cidSrc, attachments, { cidMap, cidIndex, cidCount });
    cidIndex += 1;
    if (att && att.attachmentId) {
      const url = gmailAttachmentUrl(mailboxId, messageId, att.attachmentId, apiBase);
      return `${prefix}${quote}${url}${quote}`;
    }
    return match;
  });

  // Mixed content: try https for remote signature logos (some hosts redirect).
  out = out.replace(IMG_HTTP_SRC_RE, '$1https://');

  // Hotlink blocks — only when not already set.
  out = out.replace(/<img(?![^>]*\breferrerpolicy=)/gi, '<img referrerpolicy="no-referrer" ');

  return out;
}
